import React, { lazy } from 'react';
import { ParallaxProvider } from 'react-scroll-parallax';

import frontofficeDarkThemeOptions from "./features/frontoffice/themes/darkThemeOptions";
import frontofficeLightThemeOptions from "./features/frontoffice/themes/lightThemeOptions";
import backofficeDarkThemeOptions from "./features/backoffice/themes/darkThemeOptions";
import backofficeLightThemeOptions from "./features/backoffice/themes/lightThemeOptions";

import AppAuthGuard from "./wrappers/AppAuthGuard";
import OidcAuthGuard from "./wrappers/OidcAuthGuard";
import OidcToReduxSync from "./wrappers/OidcToReduxSync";
import BottomMenuDecorator from "./wrappers/BottomMenuDecorator";
import NotificationToastsDecorator from "./wrappers/NotificationToastsDecorator";
import { OidcUserProvider } from "./contexts/OidcUserContext";
import { ExchangeProvider } from "./contexts/ExchangeContext";
import { SceneProvider } from './contexts/SceneContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';

const StorefrontSimpleLayout = lazy(() => import("./features/storefront/layouts/Simple"));
const StorefrontDefaultLayout = lazy(() => import("./features/storefront/layouts/Default"));
const StorefrontInvestmentPartyLayout = lazy(() => import("./features/storefront/layouts/Party"));
const StorefrontHomeLayout = lazy(() => import("./features/storefront/layouts/Home"));
const StorefrontContactUsLayout = lazy(() => import("./features/storefront/layouts/ContactUs"));
const FrontofficeDefaultLayout = lazy(() => import("./features/frontoffice/layouts/Default"));
const FrontofficeDiscoverLayout = lazy(() => import("./features/frontoffice/layouts/Discover"));
const FrontofficePartyLayout = lazy(() => import("./features/frontoffice/layouts/Party"));
const BackofficeLayout = lazy(() => import("./features/backoffice/layouts/Default"));

export default [
  {
    path: '/',
    layout: StorefrontSimpleLayout,
    mainElement: lazy(() => import("./features/storefront/views/Login")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [OidcUserProvider],
  },
  {
    path: '/signup',
    layout: StorefrontSimpleLayout,
    mainElement: lazy(() => import("./features/storefront/views/Signup")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [OidcUserProvider],
  },
  {
    path: '/forgot-password',
    layout: StorefrontSimpleLayout,
    mainElement: lazy(() => import("./features/storefront/views/ForgotPassword")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/change-password',
    layout: StorefrontSimpleLayout,
    mainElement: lazy(() => import("./features/storefront/views/ChangePassword")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/delete-account',
    layout: StorefrontSimpleLayout,
    mainElement: lazy(() => import("./features/storefront/views/ConfirmDeleteUser")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/privacy-policy',
    layout: StorefrontSimpleLayout,
    mainElement: lazy(() => import("./features/storefront/views/PrivacyPolicy")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/home',
    layout: StorefrontHomeLayout,
    mainElement: lazy(() => import("./features/storefront/views/Home")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [ParallaxProvider],
  },
  {
    path: '/about-us',
    layout: StorefrontDefaultLayout,
    mainElement: lazy(() => import("./features/storefront/views/AboutUs")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/frontips',
    layout: StorefrontDefaultLayout,
    mainElement: lazy(() => import("./features/storefront/views/Frontips")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/community',
    layout: StorefrontDefaultLayout,
    mainElement: lazy(() => import("./features/storefront/views/Community")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/partners',
    layout: StorefrontDefaultLayout,
    mainElement: lazy(() => import("./features/storefront/views/Partners")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/front-plus',
    layout: StorefrontDefaultLayout,
    mainElement: lazy(() => import("./features/storefront/views/FrontPlus")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [ParallaxProvider],
  },
  {
    path: '/frequently-asked-questions',
    layout: StorefrontDefaultLayout,
    mainElement: lazy(() => import("./features/storefront/views/Faq")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/contact-us',
    layout: StorefrontContactUsLayout,
    mainElement: lazy(() => import("./features/storefront/views/ContactUs")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/terms-and-conditions',
    layout: StorefrontDefaultLayout,
    mainElement: lazy(() => import("./features/storefront/views/TermsAndConditions")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/front-plus-welcome',
    layout: StorefrontDefaultLayout,
    mainElement: lazy(() => import("./features/storefront/views/FrontPlusWelcome")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/investment-parties/:investmentPartyId',
    layout: StorefrontInvestmentPartyLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/InvestmentParty")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/investment-parties-logged-in/:investmentPartyId',
    layout: FrontofficePartyLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/InvestmentParty")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/home/:activeTab',
    layout: FrontofficeDefaultLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/Events")),
    asideElement: lazy(() => import("./features/frontoffice/views/EventsAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/hashtags/:hashtagText',
    layout: FrontofficeDefaultLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/Hashtag")),
    asideElement: lazy(() => import("./features/frontoffice/views/HashtagAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/posts/:postId',
    layout: FrontofficeDefaultLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/Post")),
    asideElement: lazy(() => import("./features/frontoffice/views/PostAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator
    ],
  },
  {
    path: '/pending-deposits',
    layout: FrontofficeDefaultLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/PendingDeposits")),
    asideElement: lazy(() => import("./features/frontoffice/views/PendingDepositsAside")),
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
  },
  {
    path: '/wallet/:activeTab',
    layout: FrontofficeDefaultLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/Wallet")),
    asideElement: lazy(() => import("./features/frontoffice/views/WalletAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
      SceneProvider,
    ],
  },
  {
    path: '/complete-profile/11',
    layout: FrontofficeDefaultLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/InvestmentAccountReadinessWizardFormArgentina")),
    asideElement: lazy(() => import("./features/frontoffice/views/InvestmentAccountReadinessWizardFormAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/complete-profile/47',
    layout: FrontofficeDefaultLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/InvestmentAccountReadinessWizardFormChile")),
    asideElement: lazy(() => import("./features/frontoffice/views/InvestmentAccountReadinessWizardFormAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/goals/:activeTab',
    layout: FrontofficeDefaultLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/Goals")),
    asideElement: lazy(() => import("./features/frontoffice/views/GoalsAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/goals/:goalId/:activeTab',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/Goal")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: (params) => {
      const base = [
        OidcUserProvider,
        OidcToReduxSync,
        OidcAuthGuard,
        AppAuthGuard,
        BottomMenuDecorator,
        NotificationToastsDecorator,
        SubscriptionProvider,
      ];
      return params.activeTab === 'distribution' ? [...base, SceneProvider] : base;
    },
  },
  {
    path: '/edit-goal/:goalId/:activeTab',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/EditGoal")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/no-goal/:activeTab',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/NoGoal")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/advisor',
    layout: FrontofficeDefaultLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/Advisor")),
    asideElement: lazy(() => import("./features/frontoffice/views/AdvisorAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/watchlist',
    layout: FrontofficeDefaultLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/Watchlist")),
    asideElement: lazy(() => import("./features/frontoffice/views/WatchlistAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/bookmarks',
    layout: FrontofficeDefaultLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/Bookmarks")),
    asideElement: lazy(() => import("./features/frontoffice/views/BookmarksAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/cart',
    layout: FrontofficeDefaultLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/Cart")),
    asideElement: lazy(() => import("./features/frontoffice/views/CartAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/prices',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/Prices")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/balances',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/Balances")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/investment-items',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/InvestmentItems")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/investment-items/:investmentItemId/:activeTab',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/InvestmentItem")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
      ExchangeProvider,
    ],
  },
  {
    path: '/investors',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/Investors")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/users/:followeeUsername/:activeTab',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/User")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/edit-user/:activeTab',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/EditUser")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/brokers',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/Brokers")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/brokers/:brokerId/:activeTab',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/Broker")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/blockchain',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/Blockchain")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/boosts/:activeTab',
    layout: FrontofficeDefaultLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/Boosts")),
    asideElement: lazy(() => import("./features/frontoffice/views/BoostsAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/academy',
    layout: FrontofficeDefaultLayout,
    mainElement: lazy(() => import("./features/frontoffice/views/Academy")),
    asideElement: lazy(() => import("./features/frontoffice/views/AcademyAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/edit-user',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/EditUser")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/my-notifications',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/MyNotifications")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/my-brokers/:brokerId/dashboard',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/MyBrokersDashboard")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/my-brokers/:brokerId/operaciones',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/MyBrokersOperations")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/my-brokers/:brokerId/investment-accounts',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/MyBrokersInvestmentAccounts")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/my-brokers/:brokerId/users',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/MyBrokersUsers")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/my-brokers/:brokerId/prices',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/MyBrokersPrices")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/my-brokers/:brokerId/empleados',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/MyBrokersEmployees")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/modulos/cafci/procesos',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/CafciDashboard")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/modulos/cafci/prices',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/CafciPrecios")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/modulos/invertironline/procesos',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/InvertirOnlineDashboard")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/modulos/invertironline/prices',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/InvertirOnlinePrices")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/modulos/bind/procesos',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/BindDashboard")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/modulos/bind/transacciones',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/BindTransactions")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/dashboard',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/Dashboard")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/procesos',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/Processes")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/operaciones',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/Operations")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/users',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/Users")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/campaigns',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/Campaigns")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/empleados',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/Employees")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/tipos_de_objetivo',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/GoalTypes")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/goals',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/Goals")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/boosts',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/Boosts")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/balances',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/Balances")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/brokers',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/Brokers")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/investment-items',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/InvestmentItems")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/investment-accounts',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/InvestmentAccounts")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/planes_de_inversion',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/RiskProfiles")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/investment-parties',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/InvestmentParties")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/prices',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/Prices")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/posts',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/Posts")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/feriados',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/BankHolidays")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/eventos',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/Events")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/notifications',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./features/backoffice/views/Notifications")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  }
];